import React from "react";
import { DayPickerSingleDateController } from "react-dates";
import moment from "moment";
import * as calendarUtils from "../../utils/calendarUtils";
import { Message } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import useWindowSize from "../../utils/useWindowSize";

const Calendar = ({
  selectedDates,
  isPreferenzeValid,
  dateNonDisponibili,
  onDateChange,
}) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const isMobile = width < 768;

  //Helpers
  const isOutsideRange = (day) => {
    // Return true if date is before today
    if (day.isBefore(moment(), "day")) {
      return true;
    }

    // Return true if date is in the unavailable dates array
    if (dateNonDisponibili && dateNonDisponibili.length > 0) {
      const isUnavailable = dateNonDisponibili.some(
        (dateStr) =>
          moment(dateStr).format("YYYY-MM-DD") === day.format("YYYY-MM-DD")
      );
      if (isUnavailable) return true;
    }

    // Seasonal cutoff rule: disable dates after May 30th based on current date
    const now = moment();
    const currentMonth = now.month(); // 0-indexed (0 = January, 11 = December)

    // If current date is between January 1 and May 30 (months 0-4)
    if (currentMonth <= 4) {
      // Disable all dates after May 30th of the current year
      const cutoffDate = moment().month(4).date(30); // May 30th of current year
      if (day.isAfter(cutoffDate, "day")) {
        return true;
      }
    } else {
      // If current date is between June 1 and December 31 (months 5-11)
      // Disable all dates after May 30th of the next year
      const cutoffDate = moment().add(1, "year").month(4).date(30); // May 30th of next year
      if (day.isAfter(cutoffDate, "day")) {
        return true;
      }
    }

    return false;
  };

  // Calculate season end date based on current date
  const getSeasonEndDate = () => {
    const now = moment();
    const currentMonth = now.month(); // 0-indexed (0 = January, 11 = December)

    // If current date is between January 1 and May 30 (months 0-4)
    if (currentMonth <= 4) {
      return moment().month(4).date(30); // May 30th of current year
    } else {
      // If current date is between June 1 and December 31 (months 5-11)
      return moment().add(1, "year").month(4).date(30); // May 30th of next year
    }
  };

  // Check if all dates from today to season end are unavailable
  const areAllDatesUnavailable = () => {
    if (!dateNonDisponibili || dateNonDisponibili.length === 0) {
      return false;
    }

    const today = moment().startOf("day");
    const seasonEnd = getSeasonEndDate();

    // Generate all dates from today to season end
    const currentDate = today.clone();
    while (currentDate.isSameOrBefore(seasonEnd, "day")) {
      const dateStr = currentDate.format("YYYY-MM-DD");
      const isUnavailable = dateNonDisponibili.some(
        (unavailableDate) =>
          moment(unavailableDate).format("YYYY-MM-DD") === dateStr
      );

      // If we find at least one available date, return false
      if (!isUnavailable) {
        return false;
      }

      currentDate.add(1, "day");
    }

    // All dates are unavailable
    return true;
  };

  if (!isPreferenzeValid) {
    return (
      <div>
        <h2>{t("calendario")}</h2>
        <Message
          info
          size="large"
          style={{ textAlign: "center", padding: "2em" }}
        >
          <Message.Header style={{ fontSize: "1.3em", marginBottom: "0.5em" }}>
            {t("completa_preferenze_calendario")}
          </Message.Header>
          <p style={{ fontSize: "1.1em" }}>
            {t("completa_preferenze_calendario_desc")}
          </p>
        </Message>
      </div>
    );
  }

  // Check if all dates are unavailable
  if (areAllDatesUnavailable()) {
    return (
      <div>
        <h2>{t("calendario")}</h2>
        <Message warning>
          <Message.Header>{t("nessuna_disponibilita_stagione")}</Message.Header>
          <p>{t("nessuna_disponibilita_stagione_desc")}</p>
        </Message>
      </div>
    );
  }

  return (
    <div>
      <h2>{t("calendario")}</h2>
      <h3 style={{ marginBottom: "0.2em" }}>
        {t("seleziona_giornate_lezioni")}
      </h3>
      <p
        style={{
          marginTop: "0",
          marginBottom: "1em",
          color: "#666",
          fontSize: "0.9em",
        }}
      >
        {t("puoi_selezionare_piu_giorni")}
      </p>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          overflowX: isMobile ? "auto" : "visible",
          width: "100%",
          minHeight: "400px",
        }}
      >
        <div
          style={{
            minWidth: isMobile ? "300px" : "600px",
            width: isMobile ? "100%" : "auto",
            visibility: "visible",
            opacity: 1,
          }}
        >
          <div style={{ visibility: "visible", opacity: 1, display: "block" }}>
            <DayPickerSingleDateController
              date={null}
              onDateChange={onDateChange}
              isOutsideRange={isOutsideRange}
              isDayHighlighted={(day) =>
                selectedDates.some((day2) => calendarUtils.isSameDay(day, day2))
              }
              renderDayContents={(day) => {
                const isHighlighted = selectedDates.some((day2) =>
                  calendarUtils.isSameDay(day, day2)
                );
                return (
                  <div
                    style={{
                      borderRadius: "0%",
                      background: isHighlighted ? "#2185d0" : undefined,
                      color: isHighlighted ? "#fff" : undefined,
                      width: "32px",
                      height: "32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "auto",
                    }}
                  >
                    {day.date()}
                  </div>
                );
              }}
              numberOfMonths={isMobile ? 1 : 2}
              orientation={isMobile ? "vertical" : "horizontal"}
              hideKeyboardShortcutsPanel={true}
              noBorder={false}
              initialVisibleMonth={() => moment()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
